<template>
  <Dialog
    :visible="visible"
    modal
    :header="title"
    :style="{ width: '30rem' }"
    :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
    :closable="true"
    @update:visible="$emit('update:visible', $event)"
  >
    <div class="kb-form">
      <div class="kb-field">
        <label for="kb-search-dialog-input">{{ searchLabel }}</label>
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText
            id="kb-search-dialog-input"
            v-model="searchInput"
            size="small"
            fluid
            autofocus
            :placeholder="placeholder"
            @input="handleSearch"
            @keyup.enter="handleSearch"
          />
        </IconField>
      </div>

      <!-- Search results: the whole row picks the item -->
      <div v-if="filteredItems.length > 0">
        <p class="kb-row-meta m-0 mb-1">
          {{ filteredItems.length }} {{ filteredItems.length === 1 ? 'match' : 'matches' }}<template v-if="filteredItems.length >= resultLimit">
            · keep typing to narrow it down
          </template>
        </p>
        <ul class="kb-rows kb-search-results m-0 p-0 list-none">
          <li
            v-for="item in filteredItems"
            :key="item.id"
            class="kb-row kb-row-link"
            :class="{ 'opacity-60': loadingItemId && loadingItemId !== item.id }"
          >
            <button
              type="button"
              class="kb-row-open"
              :disabled="loadingItemId !== null"
              @click="selectItem(item)"
            >
              <span class="block text-sm text-kb-fg">{{ item.name }}</span>
              <span
                v-if="getSubtitleText(item)"
                class="kb-row-meta block"
              >{{ getSubtitleText(item) }}</span>
              <span
                v-if="isCaptainOfOtherSubkrewe(item)"
                class="block text-xs text-kb-gold"
              >
                <i class="pi pi-exclamation-triangle !text-[10px] mr-1" />Captain of {{ 'member_type' in item && item.subkrewe ? item.subkrewe.name : '' }}
              </span>
            </button>
            <div class="kb-row-aside">
              <KbSpinner
                v-if="loadingItemId === item.id"
                label="Opening"
              />
              <i
                v-else
                class="kb-row-chevron"
                :class="selectionIcon"
                aria-hidden="true"
              />
            </div>
          </li>
        </ul>
      </div>

      <p
        v-else-if="isSearchable"
        class="kb-row-meta m-0"
      >
        Nothing matches "{{ searchInput }}"
      </p>

      <p
        v-else-if="searchInput"
        class="kb-row-meta m-0"
      >
        Keep typing: at least {{ minLength }} characters
      </p>
    </div>

    <template #footer>
      <Button
        label="Cancel"
        text
        severity="secondary"
        @click="$emit('update:visible', false)"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { PropType } from 'vue'
import type { Member } from '~/shared/types/member'
import type { SubKrewe } from '~/shared/types/subkrewe'
import {
  SEARCH_DEFAULT_LIMIT,
  SEARCH_MIN_LENGTH,
  isSearchableQuery,
} from '~/shared/utils/searchText.js'

type SearchItem = Member | SubKrewe

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: 'Search',
  },
  searchLabel: {
    type: String,
    default: 'Search',
  },
  placeholder: {
    type: String,
    default: 'Type to search',
  },
  // Required, and typed to the single argument it is actually called with.
  // The old default took (items, searchTerm) but was invoked as (searchTerm),
  // so it had never worked — every consumer passes its own.
  filterFunction: {
    type: Function as PropType<(searchTerm: string) => SearchItem[]>,
    required: true,
  },
  // A hard ceiling on rendered rows. Without it, a one-character query
  // rendered every match in the roster into the DOM.
  resultLimit: {
    type: Number,
    default: SEARCH_DEFAULT_LIMIT,
  },
  minLength: {
    type: Number,
    default: SEARCH_MIN_LENGTH,
  },
  selectionIcon: {
    type: String,
    default: 'pi pi-plus',
  },
  itemType: {
    type: String,
    default: 'item',
  },
})

const emit = defineEmits(['update:visible', 'item-selected'])

const searchInput = ref('')
const filteredItems = ref<SearchItem[]>([])
const loadingItemId = ref<string | null>(null)

// Get the right text for the subtitle based on item type
const getSubtitleText = (item: SearchItem) => {
  if ('email' in item) {
    return item.email // For Member objects
  }
  else if ('description' in item) {
    return (item.description?.substring(0, 50) ?? '') + ((item.description?.length ?? 0) > 50 ? '...' : '') // For SubKrewe objects
  }
  return ''
}

// Check if a member is a captain of another subkrewe
const isCaptainOfOtherSubkrewe = (item: SearchItem): boolean => {
  // First check if this is a Member (has member_type property)
  // and that it's a captain with a subkrewe
  return 'member_type' in item
    && item.member_type === 'captain'
    && item.subkrewe !== null
    && item.subkrewe !== undefined
}

// Whether the current input is worth searching on.
const isSearchable = computed(() => isSearchableQuery(searchInput.value, props.minLength))

const handleSearch = () => {
  if (!isSearchable.value) {
    filteredItems.value = []
    return
  }

  // Passed raw: consumers normalize through the shared matcher themselves.
  filteredItems.value = props.filterFunction(searchInput.value).slice(0, props.resultLimit)
}

const selectItem = (item: SearchItem) => {
  // Set loading state for this item
  loadingItemId.value = item.id

  // Emit the selection event
  emit('item-selected', item)

  // Note: We don't reset the search state here
  // The dialog will be closed by the parent component when the operation completes
}

// Reset search when dialog closes
watch(() => props.visible, (isVisible) => {
  if (!isVisible) {
    searchInput.value = ''
    filteredItems.value = []
    loadingItemId.value = null // Reset loading state when dialog closes
  }
})

// Run search when dialog opens with existing text
watch(() => props.visible, (isVisible) => {
  if (isVisible && searchInput.value) {
    handleSearch()
  }
})
</script>

<style scoped>
/* A compact, scrolling result list */
.kb-search-results {
  max-height: 16rem;
  overflow-y: auto;
  padding-inline: 0.5rem;
  margin-inline: -0.5rem;
}

.kb-search-results .kb-row {
  padding: 0.5rem 0;
}
</style>
